import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  alpha,
  IconButton,
  Button,
  useMediaQuery,
  useTheme as useMuiTheme,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import { Close, People, Whatshot } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';

import logo from '../../assets/Text-WFHCafe_WObg.png';

interface TrendingCommunity {
  id: string;
  name: string;
  slug: string;
  description: string;
  category: string;
  membersCount: number;
  postsCount: number;
  profilePicture?: string;
  growth: number;
}

interface TopCommunitiesListModalProps {
  open: boolean;
  onClose: () => void;
  communities: TrendingCommunity[];
  loading?: boolean;
}

const TopCommunitiesListModal: React.FC<TopCommunitiesListModalProps> = ({
  open,
  onClose,
  communities,
  loading = false
}) => {
  const { mode } = useCustomTheme();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');
  const isDark = mode === 'dark';
  
  // Theme colors
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  
  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  const modalStyle = {
    borderRadius: '16px',
    ...glassEffect,
    width: isMobile ? 'calc(100vw - 32px)' : (isTablet ? '700px' : '850px'),
    maxHeight: isMobile ? '65vh' : '85vh', // Increased to show all communities
    margin: 'auto',
    padding: 0,
    overflow: 'hidden', 
    '& *': {
      scrollbarWidth: 'none',        
      msOverflowStyle: 'none',    
    },
    '& *::-webkit-scrollbar': {
      display: 'none',     
    },
  };

  const handleCommunityClick = (slug: string) => {
    onClose();
    navigate(`/community/${slug}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: modalStyle,
      }}
      scroll="paper"
    >
      {/* Main Content Container - Scrollable content */}
      <Box sx={{
        p: isMobile ? 2 : 3,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {/* Logo and Close Button Row */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: isMobile ? 2 : 3,
        }}>
          {/* Logo - Smaller for mobile */}
          <Box
            component="img"
            src={logo}
            alt="WFHCafe Logo"
            sx={{
              height: isMobile ? 40 : 50,
              width: 'auto',
              filter: isDark ? 'brightness(1.1)' : 'none',
            }}
          />
          
          {/* Close Button */}
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              padding: isMobile ? '5px' : '7px',
              '&:hover': { 
                color: goldColor,
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
              }
            }}
          >
            <Close fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Box>
        
        {/* Title - Smaller for mobile */}
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: isDark ? '#fff' : deepGreen,
            mb: 1,
            fontSize: isMobile ? '1.3rem' : '1.75rem',
          }}
        >
          Trending Communities
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
            mb: isMobile ? 2 : 3,
            fontSize: isMobile ? '0.8rem' : '0.95rem',
            lineHeight: 1.3,
          }}
        >
          Discover and join popular work-from-home communities
        </Typography>

        {/* Communities List */}
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            minHeight: '150px'
          }}>
            <CircularProgress sx={{ color: goldColor, size: isMobile ? 24 : 32 }} />
          </Box>
        ) : communities.length > 0 ? (
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            pb: 1
          }}>
            <List disablePadding>
              {communities.map((community, index) => (
                <React.Fragment key={community.id}>
                  <ListItem
                    sx={{ 
                      px: 0,
                      py: isMobile ? 1.25 : 1.75,
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                      }
                    }}
                    onClick={() => handleCommunityClick(community.slug)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {/* Rank Number - Smaller for mobile */}
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          mr: isMobile ? 1.5 : 2.5,
                          color: isDark ? goldColor : deepGreen,
                          minWidth: isMobile ? '35px' : '45px',
                          textAlign: 'center',
                          fontSize: isMobile ? '1.25rem' : '1.5rem',
                          fontWeight: 900,
                        }}
                      >
                        #{index + 1}
                      </Typography>
                      
                      {/* Community Avatar - Smaller for mobile */}
                      <ListItemAvatar sx={{ minWidth: 'auto', mr: isMobile ? 1.5 : 2.5 }}>
                        <Avatar
                          src={community.profilePicture}
                          sx={{
                            width: isMobile ? 45 : 55,
                            height: isMobile ? 45 : 55,
                            bgcolor: alpha(goldColor, 0.1),
                            color: goldColor,
                            border: `1.5px solid ${alpha(goldColor, 0.3)}`,
                            fontSize: isMobile ? '1rem' : '1.25rem',
                            fontWeight: 700,
                          }}
                        >
                          {community.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      
                      {/* Community Info - Compact for mobile */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 800,
                            mb: 0.25,
                            fontSize: isMobile ? '0.95rem' : '1.1rem',
                            color: isDark ? '#fff' : deepGreen,
                            letterSpacing: '-0.02em',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          wfh/{community.slug}
                        </Typography>
                        
                        {/* Description - Show on both mobile and desktop but truncated on mobile */}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                            fontSize: isMobile ? '0.75rem' : '0.85rem',
                            mb: 1.5,
                            lineHeight: 1.3,
                            display: '-webkit-box',
                            WebkitLineClamp: isMobile ? 1 : 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {community.description || 'A community for remote workers and coffee lovers'}
                        </Typography>
                        
                        {/* Stats - Compact for mobile */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: isMobile ? 1 : 1.5,
                          flexWrap: 'wrap',
                        }}>
                          {/* Members Count */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <People sx={{ 
                              fontSize: isMobile ? 16 : 18, 
                              color: goldColor 
                            }} />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                                fontSize: isMobile ? '0.7rem' : '0.8rem',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {community.membersCount.toLocaleString()} members
                            </Typography>
                          </Box>
                          
                          {/* Posts Count */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <Whatshot sx={{ 
                              fontSize: isMobile ? 16 : 18, 
                              color: '#FF6B6B' 
                            }} />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                                fontSize: isMobile ? '0.7rem' : '0.8rem',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {community.postsCount} posts
                            </Typography>
                          </Box>
                          
                          {/* Growth Chip - Smaller for mobile */}
                          <Chip
                            label={`+${community.growth}%`}
                            size="small"
                            sx={{
                              bgcolor: alpha(goldColor, 0.1),
                              color: goldColor,
                              fontWeight: 700,
                              height: isMobile ? '18px' : '20px',
                              fontSize: isMobile ? '0.65rem' : '0.7rem',
                              minWidth: isMobile ? '40px' : '50px',
                              '& .MuiChip-label': {
                                px: isMobile ? 0.5 : 1,
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                  
                  {/* Divider between items - Thinner for mobile */}
                  {index < communities.length - 1 && (
                    <Divider sx={{ 
                      borderColor: alpha(goldColor, 0.1),
                      my: isMobile ? 0.5 : 1,
                    }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            px: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 1.5, 
                color: isDark ? '#fff' : deepGreen, 
                fontWeight: 700,
                fontSize: isMobile ? '1.1rem' : '1.3rem',
              }}
            >
              No trending communities yet
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7), 
                mb: 2.5,
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                textAlign: 'center',
              }}
            >
              Be the first to create and grow a community!
            </Typography>
            <Button
              variant="contained"
              sx={{
                borderRadius: '50px',
                background: isDark ? 'rgba(255,255,255,0.15)' : lokiGradient,
                color: isDark ? '#fff' : goldColor,
                fontWeight: 700,
                fontSize: isMobile ? '0.85rem' : '0.95rem',
                px: isMobile ? 2.5 : 3.5,
                py: isMobile ? 0.5 : 0.75,
                minWidth: isMobile ? '130px' : '150px',
                '&:hover': {
                  background: isDark ? 'rgba(255,255,255,0.25)' : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
                  transform: 'translateY(-1px)',
                }
              }}
            >
              Create Community
            </Button>
          </Box>
        )}

        {/* Footer - Smaller for mobile */}
        <Box sx={{ 
          mt: isMobile ? 2 : 3,
          pt: isMobile ? 1.5 : 2,
          borderTop: `1px solid ${alpha(goldColor, 0.1)}`,
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: isDark ? alpha('#fff', 0.5) : alpha(deepGreen, 0.5),
              fontSize: isMobile ? '0.7rem' : '0.8rem',
              textAlign: 'center',
              display: 'block',
            }}
          >
            Showing {communities.length} trending communities • Updated daily
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TopCommunitiesListModal;